import React, { useState, useEffect } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { updateEntry } from '@/modules/billing/entries/entriesService'
import type { FinancialEntry } from '@/modules/billing/entries/entriesTypes'

interface Props {
  entry: FinancialEntry | null
  onClose: () => void
  onSaved?: () => void
  isMobile?: boolean
}

const OFFSET_OPTIONS: Array<{ value: number; labelKey: string }> = [
  { value: 0, labelKey: 'billing.entries.notify.modal.offsetOptions.atTime' },
  { value: 30, labelKey: 'billing.entries.notify.modal.offsetOptions.minutes30' },
  { value: 60, labelKey: 'billing.entries.notify.modal.offsetOptions.hour1' },
  { value: 1440, labelKey: 'billing.entries.notify.modal.offsetOptions.day1' },
  { value: 4320, labelKey: 'billing.entries.notify.modal.offsetOptions.day3' },
]

export default function NotifyModal({ entry, onClose, onSaved, isMobile = false }: Props) {
  const { t } = useTranslation()
  const { userRole } = useUnit()
  const te = (key: string, params?: Record<string, string>) => t(`billing.entries.${key}`, params)

  const [notify, setNotify] = useState<boolean>(false)
  const [notifyTime, setNotifyTime] = useState<string>('08:00')
  const [offsetMinutes, setOffsetMinutes] = useState<number>(1440)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!entry) return

    setNotify(!!(entry as any)?.notify_on_due)
    setNotifyTime(((entry as any)?.notify_time as string) ?? '08:00')
    setOffsetMinutes(Number((entry as any)?.notify_offset_minutes ?? 1440))
  }, [entry])

  if (!entry) return null

  const handleSave = async () => {
    setSaving(true)

    try {
      await updateEntry(entry.id, {
        notify_on_due: notify,
        notify_time: notifyTime,
        notify_offset_minutes: offsetMinutes,
      } as any, userRole || undefined)

      onSaved?.()
      onClose()
    } catch (err) {
      console.error('[NotifyModal] updateEntry error', err)
      alert(t('errors.defaultError'))
    } finally {
      setSaving(false)
    }
  }

  const headerRight = (
    <div className="flex items-center gap-2">
      <button
        onClick={onClose}
        className="hidden sm:flex p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
        title={te('actions.cancel')}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <button
        onClick={handleSave}
        disabled={saving}
        className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-emerald-600 dark:text-emerald-400 disabled:opacity-50"
        title={te('actions.update')}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={!!entry}
      onClose={onClose}
      title={t('billing.entries.notify.modal.title')}
      size="sm"
      mobileTopAligned={false}
      headerLeft={isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={headerRight}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t('billing.entries.notify.modal.description')}
        </p>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            className="w-4 h-4"
          />

          <span className="text-sm text-slate-700 dark:text-slate-300">
            {t('billing.entries.notify.modal.notifyLabel')}
          </span>
        </label>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">
              {t('billing.entries.notify.modal.timeLabel')}
            </label>

            <div className="relative">
              <input
                type="time"
                value={notifyTime}
                onChange={(e) => setNotifyTime(e.target.value)}
                className="notify-time-input w-full px-2 py-1 pr-9 border rounded bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />

              <svg
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-200"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="9" strokeWidth={2} />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 7v5l3 2" />
              </svg>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">
              {t('billing.entries.notify.modal.offsetLabel')}
            </label>

            <div className="flex gap-2">
              <select
                value={String(offsetMinutes)}
                onChange={(e) => setOffsetMinutes(Number(e.target.value))}
                className="px-2 py-1 border rounded bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
              >
                {OFFSET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            <p className="text-xs text-slate-500 mt-1">
              {t('billing.entries.notify.modal.offsetHelp')}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}
